// @givmo/board — recording, at a later meeting, the motion that approves an earlier meeting's minutes
// (CBG-RC11, the orchestrator's C5; backend `board_minutes.md` §A4, §A9).
//
// ══ HOW THE MINUTES PAGE HANDS OVER ══════════════════════════════════════════════════════════════
//
// A board whose rule approves its minutes by a vote at a meeting gets ONE action on the minutes page:
// "Approve them at the March 11 meeting". It opens that meeting's record with the motion dialog in its
// approval mode, by the record page's address: `?approve=<minutes record>&from=<their meeting>` (and
// `&fromSession=<segment>` for a closed session's own minutes). The address names the minutes by
// their record and their meeting only; the record page reads that meeting, and its sessions for a
// closed session's general purpose, to word what the motion approves — by public facts only (§A7),
// because the motion is part of the later meeting's record, which everyone on the board reads.
//
// The dialog sends `approves_minutes_record_id` with `presided_by_board_member_id`, which the backend
// pairs (the record of the approval names the person whose declaration concluded the vote). Its start
// asks the board's rule (§A4); a refusal is the backend's own sentence.

import { useQuery } from '@tanstack/react-query';
import { useBoardOperations } from '../../board-runtime';
import { minutesOfTheMeeting, theMinutesOf } from '../consents/minutes-consent-words';
import { closedMinutesOfTheMeeting, theClosedMinutesOf } from '../minutes/minutes-rule-words';
import { extractSegments, meetingDayOf } from './in-meeting-decode';

/** The record page's search keys for the hand-off. */
export const APPROVE_MINUTES_SEARCH = {
  record: 'approve',
  from: 'from',
  session: 'fromSession',
} as const;

/** The minutes a motion is to approve, as the address names them. */
export interface MotionApprovalTarget {
  recordId: string;
  fromMeetingId: string;
  fromSessionId: string | null;
}

function value(search: Record<string, unknown>, key: string): string | null {
  const raw = search[key];
  return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
}

/** The hand-off off the record page's search, or null when it carries none. Never throws. */
export function motionApprovalTarget(search: Record<string, unknown>): MotionApprovalTarget | null {
  const recordId = value(search, APPROVE_MINUTES_SEARCH.record);
  const fromMeetingId = value(search, APPROVE_MINUTES_SEARCH.from);
  if (recordId === null || fromMeetingId === null) return null;
  return { recordId, fromMeetingId, fromSessionId: value(search, APPROVE_MINUTES_SEARCH.session) };
}

/** What the dialog says about the minutes it approves: the "Approves" row and the motion's words. */
export interface MotionApprovalWords {
  /** "The minutes of the January board meeting, January 28, 2027", the date kept on one line. */
  approves: string;
  /**
   * "the minutes of the January board meeting of January 28, 2027", for the motion's words, which are
   * stored as the motion's text: plain spaces, never the display's kept-together date.
   */
  inASentence: string;
}

/** The "Approves" row is shown only: its date stays on one line. */
const SHOWN = { keepDateTogether: true } as const;

/**
 * The minutes' names, from their meeting's read (and its sessions', for a closed session's general
 * purpose). Null with no hand-off, and while those reads are under way; a read that fails names them
 * as well as it can.
 */
export function useMotionApprovalWords(
  orgRef: string,
  target: MotionApprovalTarget | null,
): MotionApprovalWords | null {
  const operations = useBoardOperations();
  const enabled = target !== null && orgRef.length > 0;
  const meetingQuery = useQuery({
    ...operations.officerGetBoardMeetingOptions({
      path: { org_ref: orgRef, meeting_id: target?.fromMeetingId ?? '' },
    }),
    enabled,
    retry: false,
  });
  const segmentsQuery = useQuery({
    ...operations.officerListBoardMeetingSegmentsOptions({
      path: { org_ref: orgRef, meeting_id: target?.fromMeetingId ?? '' },
    }),
    enabled: enabled && target?.fromSessionId !== null,
    retry: false,
    select: extractSegments,
  });
  if (target === null) return null;
  const pending =
    meetingQuery.isPending || (target.fromSessionId !== null && segmentsQuery.isPending);
  if (pending) return null;
  const meeting = meetingQuery.data ?? null;
  const title = meeting?.title ?? null;
  const day = meetingDayOf(meeting)?.day ?? null;
  if (target.fromSessionId === null) {
    return {
      approves: theMinutesOf(title, day, SHOWN),
      inASentence: minutesOfTheMeeting(title, day),
    };
  }
  const session = (segmentsQuery.data ?? []).find((row) => row.id === target.fromSessionId);
  const purpose = session?.purpose_label ?? null;
  return {
    approves: theClosedMinutesOf(title, day, purpose, SHOWN),
    inASentence: closedMinutesOfTheMeeting(title, day, purpose),
  };
}
